import type { RefObject } from "react";
import { useRef, useState, useEffect } from "react";

import { HOVER_DELAY_MS } from "./constants";
import type { TooltipProps } from "./tooltip";
import { isPlatformNodeJS } from "../../utils/platform.is-node";
import { isTouchDevice } from "../../utils/platform.is-touch-device";

let hasJustShowedTooltip = false;
let hasJustShowedTooltipTimeout: number | undefined;

let hasJustUsedKeyboard = false;
let hasJustUsedKeyboardTimeout: number | undefined;

/**
 * track user interactions with the DOM elements, and determine when to
 * activate the tooltip.
 */
export function useActiveState(
  props: TooltipProps,
  containerRef: RefObject<HTMLElement | SVGElement | null>,
  togglerRef: RefObject<HTMLElement | SVGElement | null>,
): boolean {
  initGlobalListeners();

  const ref = {
    current: togglerRef.current || containerRef.current,
  };

  const isHovered = useHoverState(ref, props);
  const isTouched = useTouchedState(ref, props);
  const isFocused = useFocusedState(ref, props);
  const wasJustTapped = useTappedState(ref, props);

  const active =
    !props.disabled &&
    (props.enabled ||
      isHovered ||
      isTouched ||
      (isFocused && !props.disableShowAfterFocus) ||
      wasJustTapped);

  // register when we just activated a tooltip (used to allow instant activation
  // of other tooltips when using hover)
  useEffect(() => {
    window.clearTimeout(hasJustShowedTooltipTimeout);

    if (active) {
      hasJustShowedTooltip = true;
    } else {
      hasJustShowedTooltipTimeout = window.setTimeout(() => {
        hasJustShowedTooltip = false;
      }, HOVER_DELAY_MS);
    }
  }, [active]);

  return active;
}

/**
 * determine whether the user is hovering the tooltip for so long that it
 * should be activated.
 */
function useHoverState(
  ref: React.RefObject<HTMLElement | SVGElement | null>,
  props: TooltipProps,
): boolean {
  const [isHovered, setHovered] = useState(false);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const container = ref.current;

    if (container === null || props.disabled) {
      return;
    }

    // register hover after the defined timeout when mouse enters the layer
    const handlePointerOver = (): void => {
      window.clearTimeout(timeoutRef.current);

      timeoutRef.current = window.setTimeout(
        () => {
          setHovered(true);

          window.addEventListener("scroll", handleClose, {
            capture: true,
            passive: true,
          });
        },

        // allow showing tooltip instantly on hover, if we've just displayed
        // another one (this allows user to easily browse tooltips that are
        // connected to grouped elements, for instance a collection of tools)
        !hasJustShowedTooltip ? HOVER_DELAY_MS : 0,
      );
    };

    // instantly reset hover once the mouse leaves again, when the user
    // clicks on the item or when the user scrolls on the page
    const handleClose = (): void => {
      window.clearTimeout(timeoutRef.current);
      setHovered(false);
      window.removeEventListener("scroll", handleClose, { capture: true });
    };

    container.addEventListener("pointerenter", handlePointerOver, {
      passive: true,
    });
    container.addEventListener("pointerleave", handleClose, {
      passive: true,
    });
    container.addEventListener("pointerdown", handleClose);

    return (): void => {
      container.removeEventListener("pointerenter", handlePointerOver);
      container.removeEventListener("pointerleave", handleClose);
      container.removeEventListener("pointerdown", handleClose);
      window.removeEventListener("scroll", handleClose, { capture: true });
    };
  }, [props.disabled, ref]);

  return isHovered;
}

/**
 * determine whether the user has been touching the element for long enough to
 * activate the tooltip.
 */
function useTouchedState(
  ref: React.RefObject<HTMLElement | SVGElement | null>,
  props: TooltipProps,
): boolean {
  const [isTouched, setTouched] = useState(false);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const container = ref.current;

    if (container === null || !isTouchDevice() || props.disabled) {
      // don't even attempt to use touch detection if the device isn't touch
      // based
      return;
    }

    // register touch after the user has held the element for long enough to
    // activate it
    const handleTouchStart = (evt: Event) => {
      window.clearTimeout(timeoutRef.current);

      if (evt instanceof PointerEvent && evt.type === "touch") {
        // don't handle pointer-events directly for touch-based devices, instead
        // rely on touch-events so we can detect multi-finger gestures
        return;
      }

      if (evt instanceof TouchEvent && evt.touches.length > 1) {
        // don't enable pointer events in response to multi-finger gestures
        return;
      }

      timeoutRef.current = window.setTimeout(() => {
        setTouched(true);
      }, HOVER_DELAY_MS);
    };

    // instantly reset state once the user releases the element again
    const handleTouchEnd = (): void => {
      window.clearTimeout(timeoutRef.current);
      setTouched(false);
    };

    container.addEventListener("touchstart", handleTouchStart);
    container.addEventListener("pointerdown", handleTouchEnd);
    container.addEventListener("pointerup", handleTouchEnd);
    container.addEventListener("pointercancel", handleTouchEnd);

    return (): void => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("pointerdown", handleTouchEnd);
      container.removeEventListener("pointerup", handleTouchEnd);
      container.removeEventListener("pointercancel", handleTouchEnd);
    };
  }, [props.disabled, ref]);

  return isTouched;
}

/**
 * determine whether to activate the tooltip because the user has just moved
 * focus to the container.
 */
function useFocusedState(
  ref: React.RefObject<HTMLElement | SVGElement | null>,
  props: TooltipProps,
): boolean {
  const [isFocused, setFocused] = useState(false);

  useEffect(() => {
    const container = ref.current;

    if (container === null || props.disabled) {
      return;
    }

    // register when user interacts with the UI using a pointer (used inside
    // handleFocus)
    let hasJustUsedPointer = false;
    let hasJustUsedPointerTimeout: number | undefined;

    const handlePointerDown = (): void => {
      hasJustUsedPointer = true;

      window.clearTimeout(hasJustUsedPointerTimeout);
      hasJustUsedPointerTimeout = window.setTimeout(() => {
        hasJustUsedPointer = false;
      }, 50);
    };

    // register when user activates a keyboard shortcut (used inside
    // handleFocus)
    let focusTimeout: number | undefined;
    let hasJustUsedShortcut = false;
    let hasJustUsedShortcutTimeout: number | undefined;

    const handleKeyDown = (evt: KeyboardEvent): void => {
      // assumption (1): shortcuts are always applied using a modifier key
      if (!evt.ctrlKey && !evt.shiftKey && !evt.altKey && !evt.metaKey) {
        return;
      }

      // assumption (2): shortcuts are always applied using an alphanumeric key
      // for easy recognition and cross OS support
      if (evt.key.match(/^[a-z0-9]$/i) === null) {
        return;
      }

      hasJustUsedShortcut = true;

      window.clearTimeout(hasJustUsedShortcutTimeout);
      hasJustUsedShortcutTimeout = window.setTimeout(() => {
        hasJustUsedShortcut = false;
      }, 50);
    };

    // only handle the focus event if the user hasn't just activated a shortcut
    // and/or interacted with the ui using his pointer (in which case we assume
    // he already knew the purpose of the item)
    const handleFocus = (): void => {
      if (hasJustUsedPointer || hasJustUsedShortcut) {
        return;
      }

      if (!hasJustUsedKeyboard) {
        return;
      }

      window.clearTimeout(focusTimeout);

      focusTimeout = window.setTimeout(() => {
        setFocused(true);
      }, 1);
    };

    // always allow deactivating the tooltip when the user moves focus away
    // from the element again
    const handleBlur = (): void => {
      window.clearTimeout(focusTimeout);
      setFocused(false);
    };

    // also close the tooltip in case the user clicks the element
    const handleClick = (): void => {
      window.clearTimeout(focusTimeout);
      setFocused(false);
    };

    container.addEventListener("focus", handleFocus);
    container.addEventListener("blur", handleBlur);
    container.addEventListener("click", handleClick);
    window.addEventListener("pointerdown", handlePointerDown, {
      capture: true,
    });
    window.addEventListener("keydown", handleKeyDown, {
      capture: true,
    });

    return (): void => {
      container.removeEventListener("focus", handleFocus);
      container.removeEventListener("blur", handleBlur);
      container.removeEventListener("click", handleClick);
      window.removeEventListener("pointerdown", handlePointerDown, {
        capture: true,
      });
      window.removeEventListener("keydown", handleKeyDown, {
        capture: true,
      });
    };
  }, [props.disabled, ref]);

  return isFocused;
}

/**
 * determine if the container was just tapped on a touch-based device and
 * retain the tooltip for the given amount of time afterwards
 */
function useTappedState(
  ref: React.RefObject<HTMLElement | SVGElement | null>,
  props: TooltipProps,
): boolean {
  const [wasJustTapped, setJustTapped] = useState(false);

  useEffect(() => {
    let closeTimeout: number | undefined;
    const container = ref.current;

    if (
      container === null ||
      props.showAfterTapMs === undefined ||
      props.disabled
    ) {
      return;
    }

    const handleContainerTap = () => {
      window.clearTimeout(closeTimeout);

      // register that container was tapped for the given timeout
      setJustTapped(true);

      closeTimeout = window.setTimeout(() => {
        setJustTapped(false);
        window.removeEventListener("touchstart", handleTapOutside);
      }, props.showAfterTapMs);

      // bind listener to auto-collapse the tooltip when the user clicks
      // outside the container
      window.addEventListener("touchstart", handleTapOutside);
    };

    const handleTapOutside = (evt: TouchEvent) => {
      if (isEventFiredFromContainer(evt, container)) {
        return;
      }

      // auto-reset state when user taps outside the container element again
      setJustTapped(false);

      // clean up timeouts and event listeners that are no longer needed
      window.clearTimeout(closeTimeout);
      window.removeEventListener("touchstart", handleTapOutside);
    };

    container.addEventListener("touchstart", handleContainerTap);

    return () => {
      container.removeEventListener("touchstart", handleContainerTap);
    };
  }, [props.disabled, props.showAfterTapMs, ref]);

  return wasJustTapped;
}

function isEventFiredFromContainer(evt: Event, container: Node): boolean {
  return evt.target === container || container.contains(evt.target as Node);
}

let hasBoundGlobalListener = false;

// keep track of whether or not keyboard has just been used, in order to be able
// to reject showing tooltip on-focus unless it actually happened from a
// keyboard interaction
function initGlobalListeners() {
  if (hasBoundGlobalListener || isPlatformNodeJS()) {
    return;
  }

  hasBoundGlobalListener = true;

  window.addEventListener(
    "keydown",
    () => {
      window.clearTimeout(hasJustUsedKeyboardTimeout);
      hasJustUsedKeyboard = true;

      hasJustUsedKeyboardTimeout = window.setTimeout(() => {
        hasJustUsedKeyboard = false;
      }, 100);
    },
    { capture: true },
  );
}
